/**
 * Async Sync Layer — fire-and-forget writes of local-first data (matches, ELO,
 * streaks, rivals, umpire badge) to the backend so it survives device loss and
 * follows the player across devices once they log in.
 *
 * Design:
 *  - Never blocks the UI: uses `navigator.sendBeacon` with a `fetch keepalive` fallback.
 *  - Silent: errors are swallowed; nothing thrown.
 *  - Identity: sends `X-Shadow-Id` for guests, `Authorization: Bearer …` if logged in.
 *  - Debounced state-sync so we don't flood the network when ELO/streak update rapidly.
 */
import { getShadowId } from './localStore';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('http://', 'https://');

const buildHeaders = () => {
  const h = { 'Content-Type': 'application/json' };
  try {
    const tok = localStorage.getItem('auth_token');
    if (tok) h.Authorization = `Bearer ${tok}`;
  } catch { /* ignore */ }
  h['X-Shadow-Id'] = getShadowId();
  return h;
};

/** Fire-and-forget POST that never blocks the UI. */
const post = (path, body) => {
  try {
    const url = `${BACKEND_URL}${path}`;
    const payload = JSON.stringify(body || {});
    const headers = buildHeaders();
    // fetch with keepalive: stays alive across page unloads and supports custom
    // headers (sendBeacon doesn't). Errors are silently swallowed.
    fetch(url, {
      method: 'POST',
      headers,
      body: payload,
      keepalive: true,
    }).catch(() => { /* silent */ });
  } catch { /* silent */ }
};

/** GET helper — returns null on any failure, never throws. */
const get = async (path) => {
  try {
    const url = `${BACKEND_URL}${path}`;
    const res = await fetch(url, { headers: buildHeaders() });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

// ─────────── Match Sync ───────────

/** Async-sync a completed match. Idempotent on `id`. */
export const syncMatch = (match) => {
  if (!match || !match.id) return;
  post('/api/sync/match', match);
};

/** Pull all server-side matches for the current actor (across devices). */
export const fetchSyncedMatches = () => get('/api/sync/matches');

// ─────────── Aggregate State Sync (ELO / streak / umpire / rivals) ───────────

let _pendingState = null;
let _flushTimer = null;

/** Queue a state-snapshot sync; debounced ~750ms so rapid updates coalesce. */
export const syncState = (state) => {
  _pendingState = { ..._pendingState, ...state };
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    const blob = _pendingState;
    _pendingState = null;
    _flushTimer = null;
    post('/api/sync/state', blob);
  }, 750);
};

/** Force an immediate flush of any pending state — call on page unload. */
export const flushState = () => {
  if (!_pendingState) return;
  if (_flushTimer) clearTimeout(_flushTimer);
  const blob = _pendingState;
  _pendingState = null;
  _flushTimer = null;
  post('/api/sync/state', blob);
};

/** Pull the saved snapshot — useful when a logged-in user opens on a new device. */
export const fetchSyncedState = () => get('/api/sync/state');

/** One-shot merge: after a fresh login, ask the backend to move shadow-keyed
 * data over to the user account. Idempotent. */
export const mergeShadowIntoUser = async () => {
  // Requires auth — short-circuit if not logged in.
  try {
    const tok = localStorage.getItem('auth_token');
    if (!tok) return null;
  } catch { return null; }
  return post('/api/sync/merge-shadow', {});
};

// ─────────── Publish Local Tournament ───────────

/** Push a localStorage-only tournament up so it shows publicly. */
export const publishLocalTournament = async (localTournament, extras = {}) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/tournaments/publish-local`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ ...localTournament, ...extras }),
    });
    if (!res.ok) {
      const detail = (await res.json().catch(() => ({}))).detail;
      throw new Error(detail || 'publish failed');
    }
    return await res.json();
  } catch (err) {
    return { ok: false, error: err?.message || 'unknown' };
  }
};

// Flush pending state on tab close so the latest snapshot makes it server-side.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushState);
  window.addEventListener('pagehide', flushState);
}
